import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiUploadLine } from '@remixicon/react';
import Listbox from '../../components/Listbox';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import { getImportSettings, previewRealiseImport, applyRealiseImport } from '../../api/salesBillingImport';
import './RealiseImportDialog.css';

/* IMPORTER LE RÉALISÉ (décisions d'Olivier, 2026-10-22) : un fichier Excel déposé ou des cellules collées, la même
   grille. On choisit la DONNÉE (chiffre d'affaires ou facturation), l'EXERCICE et le SENS des montants (les produits
   sont-ils en positif ou en négatif dans le fichier ? le réglage de la nature le propose, on le corrige pour ce
   chargement). L'aperçu dit ce qui sera lu, rattaché, remplacé, et refuse un fichier dont les mois portent une autre
   année que l'exercice choisi. Un chargement REMPLACE les lignes de la nature et de l'exercice. */
export default function RealiseImportDialog({ tenantId, year, kind: kindInitial = 'ca', onClose, onDone, addToast }) {
  const p = useP();
  const { i18n } = useTranslation();
  const [kind, setKind] = useState(kindInitial);
  const [annee, setAnnee] = useState(year);
  const [signs, setSigns] = useState(null);
  const [sign, setSign] = useState(null);
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const tour = useRef(0);

  useEffect(() => {
    let vivant = true;
    getImportSettings(tenantId).then((r) => {
      if (!vivant) return;
      const s = (r && r.data) || { billing: 'positive', ca: 'negative' };
      setSigns(s);
      setSign(s[kindInitial]);
    });
    return () => { vivant = false; };
  }, [tenantId]);          // eslint-disable-line

  const choisirDonnee = (k) => { setKind(k); if (signs) setSign(signs[k]); };

  /* L'aperçu se relit dès qu'un choix change ; les saisies de texte attendent un instant. */
  useEffect(() => {
    if (!sign || (!file && !text.trim())) { setPv(null); setErr(null); return undefined; }
    const n = ++tour.current;
    const t = setTimeout(async () => {
      const r = await previewRealiseImport(tenantId, { kind, year: annee, sign, file, text });
      if (n !== tour.current) return;
      if (r.error) { setPv(null); setErr(r.error); } else { setPv(r.data); setErr(null); }
    }, file ? 0 : 350);
    return () => clearTimeout(t);
  }, [tenantId, kind, annee, sign, file, text]);

  const poserFichier = useCallback((f) => { if (f) { setFile(f); setText(''); } }, []);
  const mois = (m) => new Intl.DateTimeFormat(i18n.language === 'en' ? 'en' : 'fr', { month: 'long' }).format(new Date(2026, m - 1, 1));
  const nature = kind === 'ca' ? p("chiffre d'affaires") : p('facturation');

  const charger = async () => {
    setBusy(true);
    const r = await applyRealiseImport(tenantId, { kind, year: annee, sign, file, text });
    setBusy(false);
    if (r.error) { setErr(r.error); return; }
    const d = r.data || {};
    if (addToast) {
      addToast(`${d.created} ${p('lignes chargées')}${d.replaced ? ` (${d.replaced} ${p('remplacées')})` : ''}`);
      if (d.pending) addToast(`${d.pending} ${p('libellés à rattacher')} (${p('Données & réglages › Correspondances')})`);
    }
    if (onDone) await onDone(d);
    onClose();
  };

  const annees = Array.from({ length: 5 }, (_, i) => year - 3 + i);
  const mismatch = pv && pv.year_ok === false;
  const peut = pv && !mismatch && (pv.rows || []).length > 0 && !busy;
  return (
    <div className="rid-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rid" role="dialog" aria-modal="true" aria-labelledby="rid-titre">
        <div className="rid__tt">
          <h3 id="rid-titre">{p('Importer le réalisé')}</h3>
          <button type="button" className="rid__x" aria-label={p('Fermer')} onClick={onClose}>✕</button>
        </div>
        <div className="rid__ctl">
          <SegmentedToggle label={p('Donnée')} value={kind} onChange={choisirDonnee}
                           options={[{ value: 'ca', label: p("Chiffre d'affaires") }, { value: 'billing', label: p('Facturation') }]} />
          <div className="rid__f">{p('Exercice')}
            <Listbox value={annee} onChange={(v) => setAnnee(Number(v))} options={annees.map((a) => ({ value: a, label: String(a) }))} />
          </div>
          <SegmentedToggle label={p('Produits en')} value={sign} onChange={setSign}
                           options={[{ value: 'positive', label: p('positif') }, { value: 'negative', label: p('négatif') }]} />
        </div>
        <div className="rid__src">
          <label className={`rid__drop${over ? ' is-over' : ''}`}
                 onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
                 onDrop={(e) => { e.preventDefault(); setOver(false); poserFichier(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
            <RiUploadLine size={20} aria-hidden="true" />
            {file ? <b>{file.name}</b> : <span><b>{p('Choisir un fichier Excel')}</b> {p('ou le déposer ici')}</span>}
            <input type="file" accept=".xlsx,.csv,.txt" onChange={(e) => poserFichier(e.target.files && e.target.files[0])} />
          </label>
          <textarea className="rid__txt" value={text} placeholder={p('ou coller ici les cellules copiées depuis Excel')}
                    aria-label={p('Coller les cellules')} onChange={(e) => { setText(e.target.value); setFile(null); }} />
        </div>
        {err && <p className="rid__alerte" role="alert">{err}</p>}
        {mismatch && (
          <p className="rid__alerte" role="alert">
            {(pv.warnings || [])[0]}
            {pv.years_in_file && pv.years_in_file.length === 1 && (
              <button type="button" onClick={() => setAnnee(pv.years_in_file[0])}>{p('Importer dans')} {pv.years_in_file[0]}</button>
            )}
          </p>
        )}
        {pv && !mismatch && (
          <>
            {(pv.warnings || []).map((w) => <p key={w} className="rid__avert">{w}</p>)}
            <p className="rid__sum">
              <span><b>{pv.count}</b> {p('lignes')}</span>
              <span><b>{formatNumber(pv.total, 0)}</b> CHF</span>
              <span>{mois(pv.first_month)} {p('à')} {mois(pv.last_month)} {pv.year_in_file || annee}</span>
              {pv.measure && <span>{p('Valeur lue')} : {pv.measure}</span>}
              {pv.pending > 0 && <span><b>{pv.pending}</b> {p('à rattacher')}</span>}
              {pv.ignored > 0 && <span><b>{pv.ignored}</b> {p('ignorés')}</span>}
            </p>
            <div className="rid__tab">
              <table>
                <thead><tr><th>{p('Libellé')}</th><th>{p('Offre')}</th><th className="rid__n">CHF</th></tr></thead>
                <tbody>
                  {pv.rows.map((r) => (
                    <tr key={r.label} className={r.status === 'ignored' ? 'is-muted' : ''}>
                      <td>{r.label}</td>
                      <td>{r.status === 'offer' ? r.offer_name : r.status === 'ignored' ? p('ignoré')
                        : <span className="lk-todo">{p('à rattacher')}</span>}</td>
                      <td className="rid__n">{formatNumber(r.total, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="rid__act">
          <small>
            {pv && !mismatch
              ? (pv.replaces.rows > 0
                ? `${p('Remplace')} ${pv.replaces.rows} ${p('lignes')} ${nature} ${annee} (${formatNumber(pv.replaces.total, 0)} CHF)`
                : `${p('Aucune ligne à remplacer')} (${nature} ${annee})`)
              : ''}
          </small>
          <span>
            <button type="button" className="lk-btn lk-btn--quiet" onClick={onClose}>{p('Annuler')}</button>
            <button type="button" className="rid__pri" disabled={!peut} onClick={charger}>
              {pv && pv.replaces.rows > 0 ? p('Remplacer et importer') : p('Importer')}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
